import { Component, OnInit, OnDestroy, Input, Inject, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as THREE from 'three';
import { FikoFloatingToolbarComponent } from './fiko-floating-toolbar.component';
import { FikoFloatingToolbarService } from './fiko-floating-toolbar.service';
import { FikoDialogService } from '../../../fiko-dialog-core/fiko-dialog.service';
// Dialog Components
import { FikoMetinNotuComponent } from '../../fiko-annotation/fiko-metin-notu/fiko-metin-notu.component';
import { FikoBoyutOlcusuComponent } from '../../fiko-annotation/fiko-boyut-olcusu/fiko-boyut-olcusu.component';
import { FikoOkIsaretciComponent } from '../../fiko-annotation/fiko-ok-isaretci/fiko-ok-isaretci.component';
import { FikoBayrakComponent } from '../../fiko-annotation/fiko-bayrak/fiko-bayrak.component';
import { FikoGeometrikToleransComponent } from '../../fiko-annotation/fiko-geometrik-tolerans/fiko-geometrik-tolerans.component';
import { FikoDatumOzellikComponent } from '../../fiko-annotation/fiko-datum-ozellik/fiko-datum-ozellik.component';
import { FikoYuzeyKaliteComponent } from '../../fiko-annotation/fiko-yuzey-kalite/fiko-yuzey-kalite.component';
import { FikoKaynakSembolComponent } from '../../fiko-annotation/fiko-kaynak-sembol/fiko-kaynak-sembol.component';
import { FikoCSS3DAnnotationComponent } from '../../fiko-annotation/fiko-css3d-annotation/fiko-css3d-annotation.component';
import { FikoSmartPointComponent } from '../../fiko-annotation/fiko-smart-point/fiko-smart-point.component';
import { FikoSmartPoint2DComponent } from '../../fiko-annotation/fiko-smart-point-2d/fiko-smart-point-2d.component';
// 3D Element Dialogs
import { FikoYT3dLineDialogComponent } from '../fiko-3d-elements/fikoyt-3d-line/fikoyt-3d-line-dialog.component';
import { FikoYT3dPolylineDialogComponent } from '../fiko-3d-elements/fikoyt-3d-polyline/fikoyt-3d-polyline-dialog.component';
import { FikoYT3dSplineDialogComponent } from '../fiko-3d-elements/fikoyt-3d-spline/fikoyt-3d-spline-dialog.component';
// 3D Classes
import { FikoMetinNotuCizgisi } from '../../fiko-annotation/fiko-metin-notu/FikoMetinNotuCizgisi';
import { FikoBoyutOlcusu } from '../../fiko-annotation/fiko-boyut-olcusu/FikoBoyutOlcusu';
import { FikoOkIsaretci } from '../../fiko-annotation/fiko-ok-isaretci/FikoOkIsaretci';
import { FikoBayrak } from '../../fiko-annotation/fiko-bayrak/FikoBayrak';
import { FikoGeometrikTolerans } from '../../fiko-annotation/fiko-geometrik-tolerans/FikoGeometrikTolerans';
import { FikoDatumOzellik } from '../../fiko-annotation/fiko-datum-ozellik/FikoDatumOzellik';
import { FikoYuzeyKalite } from '../../fiko-annotation/fiko-yuzey-kalite/FikoYuzeyKalite';
import { FikoKaynakSembol } from '../../fiko-annotation/fiko-kaynak-sembol/FikoKaynakSembol';

/**
 * 📝 CATIA-style Annotation Toolbar Component
 *
 * CATIA tarzı annotation araçları içeren floating toolbar
 *
 * Araçlar:
 * - 📝 Text/Note (Metin notu)
 * - 📏 Dimension (Boyut ölçüsü)
 * - 🎯 Leader (Ok işaretçi)
 * - 🏷️ Flag (Bayrak işaretleyici)
 * - 📐 Geometric Tolerance (Geometrik tolerans)
 * - 🔲 Datum Feature (Referans özellik)
 * - ⚠️ Surface Finish (Yüzey kalitesi)
 * - 🎨 Weld Symbol (Kaynak sembolü)
 */
@Component({
  selector: 'fikoyt-annotation-toolbar',
  standalone: true,
  imports: [CommonModule, FikoFloatingToolbarComponent],
  template: `
<!-- Ana Annotation Toolbar - Başlıksız, Pill Şeklinde, Başlangıçta Gizli -->
<fikoyt-floating-toolbar
  [toolbarId]="toolbarId"
  [baslikGorunur]="false"
  [handleGorunur]="false"
  [kapatButonuGorunur]="false"
  [ozelSinif]="'basliksiz-toolbar annotation-toolbar'"
  [gorunur]="false">

  <div class="annotation-tools">
    <!-- Text/Note -->
    <button class="circular ui icon yellow mini button"
            (click)="selectTool('text')"
            [class.active]="activeTool === 'text'"
            title="Text/Note (Metin Notu)">
      <i class="icon font"></i>
    </button>

    <!-- Dimension -->
    <button class="circular ui icon blue mini button"
            (click)="selectTool('dimension')"
            [class.active]="activeTool === 'dimension'"
            title="Dimension (Boyut Ölçüsü)">
      <i class="icon arrows alternate horizontal"></i>
    </button>

    <!-- Leader/Arrow -->
    <button class="circular ui icon green mini button"
            (click)="selectTool('leader')"
            [class.active]="activeTool === 'leader'"
            title="Leader (Ok İşaretçi)">
      <i class="icon long arrow alternate right"></i>
    </button>

    <!-- Flag/Marker -->
    <button class="circular ui icon red mini button"
            (click)="selectTool('flag')"
            [class.active]="activeTool === 'flag'"
            title="Flag (Bayrak İşaretleyici)">
      <i class="icon flag"></i>
    </button>

    <!-- Geometric Tolerance -->
    <button class="circular ui icon purple mini button"
            [class.fiko-kilit]="!lisans('gdt')"
            (click)="selectTool('tolerance')"
            [class.active]="activeTool === 'tolerance'"
            title="Geometric Tolerance (Geometrik Tolerans)">
      <i class="icon crosshairs"></i>
    </button>

    <!-- Datum Feature -->
    <button class="circular ui icon teal mini button"
            [class.fiko-kilit]="!lisans('gdt')"
            (click)="selectTool('datum')"
            [class.active]="activeTool === 'datum'"
            title="Datum Feature (Referans Özellik)">
      <i class="icon circle outline"></i>
    </button>

    <!-- Surface Finish -->
    <button class="circular ui icon orange mini button surface-finish-btn"
            [class.fiko-kilit]="!lisans('gdt')"
            (click)="selectTool('surface')"
            [class.active]="activeTool === 'surface'"
            title="Surface Finish (Yüzey Kalitesi)">
      <img src="./simgeler/surface-finish-icon.svg" alt="Surface Finish" class="custom-icon">
    </button>

    <!-- Weld Symbol -->
    <button class="circular ui icon brown mini button"
            [class.fiko-kilit]="!lisans('gdt')"
            (click)="selectTool('weld')"
            [class.active]="activeTool === 'weld'"
            title="Weld Symbol (Kaynak Sembolü)">
      <i class="icon fire"></i>
    </button>

    <!-- CSS3D Annotation -->
    <button class="circular ui icon pink mini button"
            (click)="selectTool('css3d')"
            [class.active]="activeTool === 'css3d'"
            title="CSS3D Annotation (Profesyonel Annotation)">
      <i class="icon tag"></i>
    </button>

    <!-- Smart Point 3D (CSS3D) -->
    <button class="circular ui icon olive mini button"
            (click)="selectTool('smartpoint3d')"
            [class.active]="activeTool === 'smartpoint3d'"
            title="Smart Point 3D (CSS3D - Derinlik Sıralamalı)">
      <i class="icon bullseye"></i>
    </button>

    <!-- Smart Point 2D (CSS2D) -->
    <button class="circular ui icon olive mini button"
            style="opacity: 0.7;"
            (click)="selectTool('smartpoint2d')"
            [class.active]="activeTool === 'smartpoint2d'"
            title="Smart Point 2D (CSS2D - Klasik)">
      <i class="icon dot circle outline"></i>
    </button>

    <!-- Separator - 3D Elements -->
    <div style="width: 1px; height: 20px; background: rgba(255,255,255,0.2); margin: 0 4px;"></div>

    <!-- 3D Line -->
    <button class="circular ui icon mini button"
            style="background: linear-gradient(135deg, #00CED1 0%, #008B8B 100%); color: #fff;"
            (click)="selectTool('line3d')"
            [class.active]="activeTool === 'line3d'"
            title="3D Cizgi">
      <i class="icon minus"></i>
    </button>

    <!-- 3D Polyline -->
    <button class="circular ui icon mini button"
            style="background: linear-gradient(135deg, #00FFFF 0%, #008080 100%); color: #fff;"
            (click)="selectTool('polyline3d')"
            [class.active]="activeTool === 'polyline3d'"
            title="3D Polyline">
      <i class="icon chart line"></i>
    </button>

    <!-- 3D Spline -->
    <button class="circular ui icon mini button"
            style="background: linear-gradient(135deg, #FFA500 0%, #FF6600 100%); color: #fff;"
            (click)="selectTool('spline3d')"
            [class.active]="activeTool === 'spline3d'"
            title="3D Spline (Catmull-Rom / Bezier)">
      <i class="icon wave square"></i>
    </button>

    <!-- Separator -->
    <div style="width: 1px; height: 20px; background: rgba(255,255,255,0.2); margin: 0 4px;"></div>

    <!-- Clear All -->
    <button class="circular ui icon grey mini button"
            (click)="clearAll()"
            title="Tümünü Temizle">
      <i class="icon eraser"></i>
    </button>

    <!-- Settings -->
    <button class="circular ui icon grey mini button"
            (click)="openSettings()"
            title="Ayarlar">
      <i class="icon cog"></i>
    </button>
  </div>
</fikoyt-floating-toolbar>
  `,
  styles: [`
    .annotation-tools {
      display: flex;
      flex-direction: row;
      gap: 4px;
      align-items: center;
      padding: 0;
    }

    .annotation-tools .button {
      transition: all 0.2s ease;
      position: relative;
    }

    .annotation-tools .button.active {
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8) inset !important;
      transform: scale(1.1);
    }

    .annotation-tools .button.active::after {
      content: '';
      position: absolute;
      bottom: -3px;
      left: 50%;
      transform: translateX(-50%);
      width: 60%;
      height: 2px;
      background: white;
      border-radius: 1px;
    }

    .annotation-tools .button:hover {
      transform: scale(1.15);
    }

    /* Toolbar özel stilleri */
    :host ::ng-deep .annotation-toolbar .fiko-toolbar-container {
      background-color: rgba(0, 127, 127, 0.85) !important;
      border: 1px solid rgba(127, 255, 255, 0.35) !important;
      box-shadow: 0px 0px 12px rgb(0 255 255 / 70%) !important;
      pointer-events: none !important; /* Container'ı tıklanamaz yap */
      padding: 8px 12px !important; /* İçerik için padding */
      height: auto !important; /* İçeriğe göre yükseklik */
      width: auto !important; /* İçeriğe göre genişlik */
    }

    :host ::ng-deep .annotation-toolbar .fiko-toolbar-container:hover {
      box-shadow: 0px 0px 16px rgb(0 255 255 / 90%) !important;
    }

    /* Toolbar modal wrapper */
    :host ::ng-deep .annotation-toolbar {
      pointer-events: none !important;
      position: fixed !important;
      bottom: 20px !important;
      left: 50% !important;
      transform: translateX(-50%) !important;
    }

    /* İçerideki butonlar tıklanabilir olmalı */
    .annotation-tools {
      pointer-events: all !important;
    }

    .annotation-tools .button {
      pointer-events: all !important;
    }

    /* Custom icon styling */
    .custom-icon {
      width: 16px;
      height: 16px;
      display: inline-block;
      vertical-align: middle;
      filter: none; /* Orijinal renkli görünüm */
    }

    .surface-finish-btn .custom-icon {
      filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.5)); /* Hafif parlama efekti */
    }
  `]
})
export class FikoAnnotationToolbarComponent implements OnInit, OnDestroy {
  @Input() toolbarId: string = 'annotationTools';
  @Input() pencere?: any; // Dialog pencere referansı
  @Input() anaGosterim?: any; // FikoThreeJsComponent referansı

  activeTool: string | null = null;

  /** GD&T / PMI sembol araçları lisansı (OLCUM:gdt; köprü yoksa AÇIK — geriye uyumlu). */
  lisans(komut: string): boolean { try { return (window as any).__fikoModul?.('OLCUM', komut) ?? true; } catch { return true; } }

  // Smart Point düzenleme subscription
  private smartPointEditSubscription: any;

  constructor(
    private toolbarService: FikoFloatingToolbarService,
    @Inject(forwardRef(() => FikoDialogService)) private DialogFiko: FikoDialogService
  ) {}

  ngOnInit(): void {
    // Smart Point sağ tık düzenleme event'ini dinle
    if (this.anaGosterim?.smartPointEditRequest) {
      this.smartPointEditSubscription = this.anaGosterim.smartPointEditRequest.subscribe((nokta: any) => {
        this.openSmartPoint3DDialogForEdit(nokta);
      });
    }
  }

  ngOnDestroy(): void {
    if (this.smartPointEditSubscription) {
      this.smartPointEditSubscription.unsubscribe();
    }
  }

  /**
   * Toolbar'ı aç/kapat
   */
  public toggle(): void {
    console.log('[AnnotationToolbar] toggle() çağrıldı, toolbarId:', this.toolbarId);
    const toolbar = this.toolbarService.bul(this.toolbarId);
    console.log('[AnnotationToolbar] Service\'den bulunan toolbar:', toolbar);
    if (toolbar) {
      console.log('[AnnotationToolbar] toolbar.toggle() çağrılıyor...');
      toolbar.toggle();
    } else {
      console.warn('[AnnotationToolbar] Toolbar bulunamadı! Kayıtlı ID\'ler:', this.toolbarService.tumIdleriAl());
    }
  }

  /**
   * Toolbar'ı aç
   */
  public ac(): void {
    const toolbar = this.toolbarService.bul(this.toolbarId);
    if (toolbar) {
      toolbar.ac();
    }
  }

  /**
   * Toolbar'ı kapat
   */
  public kapa(): void {
    const toolbar = this.toolbarService.bul(this.toolbarId);
    if (toolbar) {
      toolbar.kapa();
    }
  }

  /**
   * Araç seçildi
   */
  selectTool(tool: string): void {
    // GD&T / PMI sembol araçları lisansa bağlı (kilitliyse engelle)
    if (['tolerance', 'datum', 'surface', 'weld'].includes(tool)) {
      if (!this.lisans('gdt')) return;
      try { (window as any).__fikoKampanyaBildir?.('OLCUM', 'gdt', 'GD&T / PMI'); } catch { }
    }
    if (this.activeTool === tool) {
      // Aynı araca tıklanırsa deaktive et
      this.activeTool = null;
    } else {
      // Yeni araç aktive et
      this.activeTool = tool;

      // Burada ilgili annotation fonksiyonunu çağır
      this.executeToolAction(tool);
    }
  }

  /**
   * Araç aksiyonunu çalıştır - Professional Click-to-Place System
   */
  private executeToolAction(tool: string): void {
    const sahne = this.anaGosterim?.sahneler?.[0];
    if (!sahne?.fikoEtiketler) {
      return;
    }

    switch (tool) {
      case 'text':
        this.openMetinNotuDialog();
        break;

      case 'dimension':
        this.openBoyutOlcusuDialog();
        break;

      case 'leader':
        this.openOkIsaretciDialog();
        break;

      case 'flag':
        this.openBayrakDialog();
        break;

      case 'tolerance':
        this.openGeometrikToleransDialog();
        break;

      case 'datum':
        this.openDatumOzellikDialog();
        break;

      case 'surface':
        this.openYuzeyKaliteDialog();
        break;

      case 'weld':
        this.openKaynakSembolDialog();
        break;

      case 'css3d':
        this.openCSS3DAnnotationDialog();
        break;

      case 'smartpoint3d':
        this.openSmartPoint3DDialog();
        break;

      case 'smartpoint2d':
        this.openSmartPoint2DDialog();
        break;

      case 'line3d':
        this.openLine3DDialog();
        break;

      case 'polyline3d':
        this.openPolyline3DDialog();
        break;

      case 'spline3d':
        this.openSpline3DDialog();
        break;
    }
  }

  /**
   * Tek nokta ile annotation ekle (click-to-place)
   */
  private startSinglePointPlacement(tool: string): void {
    if (!this.anaGosterim) return;

    // Cursor'ı crosshair yap
    const canvas = this.anaGosterim.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'crosshair';
    }

    // Tek tıklama event handler
    const clickHandler = (event: MouseEvent) => {
      const sahne = this.anaGosterim?.sahneler?.[0];
      if (!sahne) return;

      // 3D pozisyon hesapla
      const position = this.get3DPositionFromMouse(event);
      if (!position) {
        return;
      }

      // Annotation'ı oluştur ve sahneye ekle
      let annotation: any;
      const etiketler = sahne.fikoEtiketler;
      const parentEtiket = etiketler.children[0] || etiketler;

      switch (tool) {
        case 'flag':
          annotation = new FikoBayrak(sahne, position, 'Yeni bayrak', '#FF0000');
          const flagEtiket = etiketler.ekleBayrak();
          flagEtiket.userData['FikoMetinNotuCizgisi'] = annotation;
          annotation.parent = flagEtiket;
          sahne.add(annotation);
          break;

        case 'tolerance':
          annotation = new FikoGeometrikTolerans(sahne, position, 'flatness', 0.01);
          const toleranceEtiket = etiketler.ekleGeometrikTolerans();
          toleranceEtiket.userData['FikoAnnotation'] = annotation;
          annotation.parent = toleranceEtiket;
          sahne.add(annotation);
          break;

        case 'datum':
          annotation = new FikoDatumOzellik(sahne, position, 'A');
          const datumEtiket = etiketler.ekleDatumOzellik();
          datumEtiket.userData['FikoAnnotation'] = annotation;
          annotation.parent = datumEtiket;
          sahne.add(annotation);
          break;

        case 'surface':
          annotation = new FikoYuzeyKalite(sahne, position, 3.2, 'machining');
          const surfaceEtiket = etiketler.ekleYuzeyKalite();
          surfaceEtiket.userData['FikoAnnotation'] = annotation;
          annotation.parent = surfaceEtiket;
          sahne.add(annotation);
          break;

        case 'weld':
          annotation = new FikoKaynakSembol(sahne, position, 'fillet', 5);
          const weldEtiket = etiketler.ekleKaynakSembol();
          weldEtiket.userData['FikoAnnotation'] = annotation;
          annotation.parent = weldEtiket;
          sahne.add(annotation);
          break;
      }

      // Render güncelle
      this.anaGosterim?.ciz();
      this.anaGosterim?.FikoYT?.degisti?.emit();

      // Event handler'ı temizle ve cursor'ı normal yap
      if (canvas) {
        canvas.removeEventListener('click', clickHandler);
        canvas.style.cursor = 'default';
      }

      // Tool'u deaktive et
      this.activeTool = null;
    };

    // Click handler'ı ekle
    if (canvas) {
      canvas.addEventListener('click', clickHandler, { once: true });
    }
  }

  /**
   * İki nokta ile annotation ekle (dimension, leader)
   */
  private startTwoPointPlacement(tool: string): void {
    if (!this.anaGosterim) return;

    const canvas = this.anaGosterim.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'crosshair';
    }

    let firstPoint: THREE.Vector3 | null = null;

    const clickHandler = (event: MouseEvent) => {
      const sahne = this.anaGosterim?.sahneler?.[0];
      if (!sahne) return;

      const position = this.get3DPositionFromMouse(event);
      if (!position) return;

      if (!firstPoint) {
        // İlk nokta
        firstPoint = position;
      } else {
        // İkinci nokta - annotation'ı oluştur
        let annotation: any;
        const etiketler = sahne.fikoEtiketler;

        switch (tool) {
          case 'dimension':
            annotation = new FikoBoyutOlcusu(sahne, firstPoint, position, { renk: '#00FFFF', birim: 'mm' });
            const dimEtiket = etiketler.ekleBoyutOlcusu();
            dimEtiket.userData['FikoAnnotation'] = annotation;
            annotation.parent = dimEtiket;
            sahne.add(annotation);
            break;

          case 'leader':
            annotation = new FikoOkIsaretci(sahne, firstPoint, position, { renk: '#00FF00', metin: 'Yeni ok' });
            const leaderEtiket = etiketler.ekleOkIsaretci();
            leaderEtiket.userData['FikoAnnotation'] = annotation;
            annotation.parent = leaderEtiket;
            sahne.add(annotation);
            break;
        }

        // Render güncelle
        this.anaGosterim?.ciz();
        this.anaGosterim?.FikoYT?.degisti?.emit();

        // Cleanup
        if (canvas) {
          canvas.removeEventListener('click', clickHandler);
          canvas.style.cursor = 'default';
        }
        this.activeTool = null;
      }
    };

    if (canvas) {
      canvas.addEventListener('click', clickHandler);
    }
  }

  /**
   * Mouse pozisyonundan 3D world pozisyonu hesapla
   */
  private get3DPositionFromMouse(event: MouseEvent): THREE.Vector3 | null {
    if (!this.anaGosterim?.sunucu || !this.anaGosterim?.kamera) return null;

    const canvas = this.anaGosterim.sunucu.domElement;
    const rect = canvas.getBoundingClientRect();

    // Normalized device coordinates (-1 to +1)
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    // Raycaster ile 3D pozisyon hesapla
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.anaGosterim.kamera);

    const sahne = this.anaGosterim.sahneler?.[0];
    if (!sahne) return null;

    // Mesh'lere raycast
    const meshes = sahne.children.filter((obj: any) => obj.isMesh || obj.type === 'Mesh');
    const intersects = raycaster.intersectObjects(meshes, true);

    if (intersects.length > 0) {
      return intersects[0].point.clone();
    }

    // Mesh yoksa kamera önünde varsayılan pozisyon
    const distance = 5;
    const direction = new THREE.Vector3();
    this.anaGosterim.kamera.getWorldDirection(direction);
    const position = this.anaGosterim.kamera.position.clone();
    position.add(direction.multiplyScalar(distance));

    return position;
  }

  /**
   * Tümünü temizle
   */
  clearAll(): void {
    this.activeTool = null;

    // Sahneyi al
    const sahne = this.anaGosterim?.sahneler?.[0];
    if (!sahne?.fikoEtiketler) {
      return;
    }

    // Tüm etiketleri temizle
    sahne.fikoEtiketler.tumunuTemizle();

    // Sahneyi güncelle
    if (this.anaGosterim) {
      this.anaGosterim.ciz();
      this.anaGosterim.FikoYT?.degisti?.emit();
    }
  }

  /**
   * Metin notu dialogunu aç
   */
  openMetinNotuDialog(): void {
    if (!this.anaGosterim) {
      return;
    }

    this.DialogFiko.ac({
      bilesen: FikoMetinNotuComponent,
      id: 'fiko-metin-notu-dialog',
      ayarlar: {
        baslik: 'Metin Notu',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 340, h: 'auto' }
      },
      parametreler: {
        anaGosterim: this.anaGosterim
      }
    });
  }

  /**
   * Boyut ölçüsü dialogunu aç
   */
  openBoyutOlcusuDialog(): void {
    if (!this.anaGosterim) return;

    this.DialogFiko.ac({
      bilesen: FikoBoyutOlcusuComponent,
      id: 'fiko-boyut-olcusu-dialog',
      ayarlar: {
        baslik: 'Boyut Ölçüsü',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 340, h: 'auto' }
      },
      parametreler: {
        anaGosterim: this.anaGosterim
      }
    });
  }

  /**
   * Ok işaretçi dialogunu aç
   */
  openOkIsaretciDialog(): void {
    if (!this.anaGosterim) return;

    this.DialogFiko.ac({
      bilesen: FikoOkIsaretciComponent,
      id: 'fiko-ok-isaretci-dialog',
      ayarlar: {
        baslik: 'Ok İşaretçi',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 340, h: 'auto' }
      },
      parametreler: {
        anaGosterim: this.anaGosterim
      }
    });
  }

  /**
   * Bayrak dialogunu aç
   */
  openBayrakDialog(): void {
    if (!this.anaGosterim) return;

    this.DialogFiko.ac({
      bilesen: FikoBayrakComponent,
      id: 'fiko-bayrak-dialog',
      ayarlar: {
        baslik: 'Bayrak İşaretleyici',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 340, h: 'auto' }
      },
      parametreler: {
        anaGosterim: this.anaGosterim
      }
    });
  }

  /**
   * Geometrik tolerans dialogunu aç
   */
  openGeometrikToleransDialog(): void {
    if (!this.anaGosterim) return;

    this.DialogFiko.ac({
      bilesen: FikoGeometrikToleransComponent,
      id: 'fiko-geometrik-tolerans-dialog',
      ayarlar: {
        baslik: 'Geometrik Tolerans',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 360, h: 'auto' }
      },
      parametreler: {
        anaGosterim: this.anaGosterim
      }
    });
  }

  /**
   * Datum özellik dialogunu aç
   */
  openDatumOzellikDialog(): void {
    if (!this.anaGosterim) return;

    this.DialogFiko.ac({
      bilesen: FikoDatumOzellikComponent,
      id: 'fiko-datum-ozellik-dialog',
      ayarlar: {
        baslik: 'Datum Özellik',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 340, h: 'auto' }
      },
      parametreler: {
        anaGosterim: this.anaGosterim
      }
    });
  }

  /**
   * Yüzey kalitesi dialogunu aç
   */
  openYuzeyKaliteDialog(): void {
    if (!this.anaGosterim) return;

    this.DialogFiko.ac({
      bilesen: FikoYuzeyKaliteComponent,
      id: 'fiko-yuzey-kalite-dialog',
      ayarlar: {
        baslik: 'Yüzey Kalitesi',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 360, h: 'auto' }
      },
      parametreler: {
        anaGosterim: this.anaGosterim
      }
    });
  }

  /**
   * Kaynak sembolü dialogunu aç
   */
  openKaynakSembolDialog(): void {
    if (!this.anaGosterim) return;

    this.DialogFiko.ac({
      bilesen: FikoKaynakSembolComponent,
      id: 'fiko-kaynak-sembol-dialog',
      ayarlar: {
        baslik: 'Kaynak Sembolü',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 360, h: 'auto' }
      },
      parametreler: {
        anaGosterim: this.anaGosterim
      }
    });
  }

  /**
   * CSS3D Annotation dialogunu aç - Profesyonel CAD-style annotation
   */
  openCSS3DAnnotationDialog(): void {
    if (!this.anaGosterim) return;

    this.DialogFiko.ac({
      bilesen: FikoCSS3DAnnotationComponent,
      id: 'fiko-css3d-annotation-dialog',
      ayarlar: {
        baslik: 'CSS3D Annotation',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 380, h: 'auto' }
      },
      parametreler: {
        anaGosterim: this.anaGosterim
      }
    });
  }

  /**
   * Smart Point 3D dialogunu aç - CSS3D Renderer (Derinlik Sıralamalı)
   */
  openSmartPoint3DDialog(): void {
    if (!this.anaGosterim) return;

    this.DialogFiko.ac({
      bilesen: FikoSmartPointComponent,
      id: 'fiko-smart-point-3d-dialog',
      ayarlar: {
        baslik: 'Smart Point 3D',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 200, h: 'auto' }
      },
      parametreler: {
        anaGosterim: this.anaGosterim
      }
    });
  }

  /**
   * Smart Point 3D dialogunu düzenleme modu ile aç
   * Sağ tıklama ile tetiklenir
   */
  openSmartPoint3DDialogForEdit(nokta: any): void {
    if (!this.anaGosterim) return;

    // Düzenlenecek noktayı anaGosterim'e kaydet - dialog component bunu kontrol edecek
    this.anaGosterim.pendingSmartPointEdit = nokta;

    // Dialog'u aç
    this.DialogFiko.ac({
      bilesen: FikoSmartPointComponent,
      id: 'fiko-smart-point-3d-dialog',
      ayarlar: {
        baslik: 'Smart Point 3D - Düzenle',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 200, h: 'auto' }
      },
      parametreler: {
        anaGosterim: this.anaGosterim
      }
    });
  }

  /**
   * Smart Point 2D dialogunu aç - CSS2D Renderer (Klasik)
   */
  openSmartPoint2DDialog(): void {
    if (!this.anaGosterim) return;

    this.DialogFiko.ac({
      bilesen: FikoSmartPoint2DComponent,
      id: 'fiko-smart-point-2d-dialog',
      ayarlar: {
        baslik: 'Smart Point 2D',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 200, h: 'auto' }
      },
      parametreler: {
        anaGosterim: this.anaGosterim
      }
    });
  }

  /**
   * 3D Line dialogunu ac
   */
  openLine3DDialog(): void {
    if (!this.anaGosterim) return;

    this.DialogFiko.ac({
      bilesen: FikoYT3dLineDialogComponent,
      id: 'fiko-3d-line-dialog',
      ayarlar: {
        baslik: '3D Cizgi',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 240, h: 'auto' }
      },
      parametreler: {
        sahne: this.anaGosterim?.sahneler?.[0]
      }
    });
  }

  /**
   * 3D Polyline dialogunu ac
   */
  openPolyline3DDialog(): void {
    if (!this.anaGosterim) return;

    this.DialogFiko.ac({
      bilesen: FikoYT3dPolylineDialogComponent,
      id: 'fiko-3d-polyline-dialog',
      ayarlar: {
        baslik: '3D Polyline',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 240, h: 'auto' }
      },
      parametreler: {
        sahne: this.anaGosterim?.sahneler?.[0]
      }
    });
  }

  /**
   * 3D Spline dialogunu ac - Catmull-Rom ve Bezier egrileri
   */
  openSpline3DDialog(): void {
    if (!this.anaGosterim) return;

    this.DialogFiko.ac({
      bilesen: FikoYT3dSplineDialogComponent,
      id: 'fiko-3d-spline-dialog',
      ayarlar: {
        baslik: '3D Spline',
        modalMi: false,
        sinirlandi: false,
        boyut: { w: 260, h: 'auto' }
      },
      parametreler: {
        sahne: this.anaGosterim?.sahneler?.[0]
      }
    });
  }

  /**
   * Ayarlar dialogunu aç
   */
  openSettings(): void {
    // TODO: Ayarlar dialogunu aç
  }
}
